import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PRODUCT_STATUS, PRODUCT_STATUS_REVERSE } from '../../utils/productStatus';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.iraq-parts.com/api';

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [carModels, setCarModels] = useState([]);
  const [carYears, setCarYears] = useState([]);
  const [carBrands, setCarBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [additionalImages, setAdditionalImages] = useState([]);
  const [additionalImageFiles, setAdditionalImageFiles] = useState([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCarModel, setFilterCarModel] = useState('');
  const [filterCarBrand, setFilterCarBrand] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    barcode: '',
    size: '',
    car_models: [],
    car_years: [],
    car_brands: [],
    product_status: 'original_new',
    purchase_price: '',
    sell_price: '',
    wholesale_price: '',
    discount: 0,
    store_status: 'active',
    wholesale_status: 'inactive',
    stock_quantity: 0,
    supplier: '',
    category_id: '',
    weight: '',
    description: '',
    image_url: ''
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchCarModels();
    fetchCarYears();
    fetchCarBrands();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, searchQuery, filterCategory, filterStatus, filterCarModel, filterCarBrand]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterCategory, filterStatus, filterCarModel, filterCarBrand]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use admin endpoint to get all product details including sensitive data
      const response = await axios.get(`${API_URL}/products/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchCarModels = async () => {
    try {
      const response = await axios.get(`${API_URL}/car-models`);
      setCarModels(response.data.data);
    } catch (error) {
      console.error('Error fetching car models:', error);
    }
  };

  const fetchCarYears = async () => {
    try {
      const response = await axios.get(`${API_URL}/car-years`);
      setCarYears(response.data.data);
    } catch (error) {
      console.error('Error fetching car years:', error);
    }
  };

  const fetchCarBrands = async () => {
    try {
      const response = await axios.get(`${API_URL}/car-brands`);
      setCarBrands(response.data.data);
    } catch (error) {
      console.error('Error fetching car brands:', error);
    }
  };

  const filterProducts = () => {
    let filtered = [...products];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(query) ||
        product.sku?.toLowerCase().includes(query) ||
        product.barcode?.toLowerCase().includes(query) ||
        product.supplier?.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (filterCategory) {
      filtered = filtered.filter(product => product.category_id === parseInt(filterCategory));
    }

    // Status filter
    if (filterStatus) {
      filtered = filtered.filter(product => product.store_status === filterStatus);
    }

    // Car Model filter
    if (filterCarModel) {
      const modelId = parseInt(filterCarModel);
      filtered = filtered.filter(product => {
        try {
          const models = typeof product.car_models === 'string' 
            ? JSON.parse(product.car_models) 
            : product.car_models;
          return Array.isArray(models) && models.includes(modelId);
        } catch (e) {
          return false;
        }
      });
    }

    // Car Brand filter
    if (filterCarBrand) {
      const brandId = parseInt(filterCarBrand);
      filtered = filtered.filter(product => {
        try {
          const brands = typeof product.car_brands === 'string' 
            ? JSON.parse(product.car_brands) 
            : product.car_brands;
          return Array.isArray(brands) && brands.includes(brandId);
        } catch (e) {
          return false;
        }
      });
    }

    setFilteredProducts(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const ensureArray = (value) => {
    if (Array.isArray(value)) return value;
    if (!value || value === '' || value === 'null' || value === null || value === undefined) return [];
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const handleCheckboxChange = (fieldName, itemId) => {
    const currentValues = ensureArray(formData[fieldName]);
    const numericId = parseInt(itemId);
    
    let updatedValues;
    if (currentValues.includes(numericId)) {
      // Remove if already selected
      updatedValues = currentValues.filter(id => id !== numericId);
    } else {
      // Add if not selected
      updatedValues = [...currentValues, numericId];
    }
    
    setFormData(prev => ({ ...prev, [fieldName]: updatedValues }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create local preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAdditionalImageFiles(prev => [...prev, ...files]);
      // Create preview URLs
      const previews = files.map(file => URL.createObjectURL(file));
      setAdditionalImages(prev => [...prev, ...previews]);
    }
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
    setAdditionalImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  const openModal = async (product = null) => {
    if (product) {
      // Fetch full product details including images
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/products/admin/${product.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const fullProduct = response.data.data;
        
        setEditingProduct(fullProduct);
        setImagePreview(fullProduct.image_url || '');
        
        // Load existing additional images if available
        if (fullProduct.images && Array.isArray(fullProduct.images)) {
          const imageUrls = fullProduct.images.map(img => img.image_url);
          setAdditionalImages(imageUrls);
        } else {
          setAdditionalImages([]);
        }
        
        // Safely parse JSON fields and ensure numeric IDs
        const parseJsonField = (field) => {
          if (!field || field === '' || field === 'null') return [];
          
          // If it's already an array, just ensure numeric IDs
          if (Array.isArray(field)) {
            return field.map(id => parseInt(id));
          }
          
          // If it's a string, try to parse it
          if (typeof field === 'string') {
            try {
              const parsed = JSON.parse(field);
              return Array.isArray(parsed) ? parsed.map(id => parseInt(id)) : [];
            } catch (e) {
              console.warn('Failed to parse JSON field:', field, e);
              return [];
            }
          }
          
          return [];
        };
        
        setFormData({
          name: fullProduct.name || '',
          sku: fullProduct.sku || '',
          barcode: fullProduct.barcode || '',
          size: fullProduct.size || '',
          car_models: parseJsonField(fullProduct.car_models),
          car_years: parseJsonField(fullProduct.car_years),
          car_brands: parseJsonField(fullProduct.car_brands),
          product_status: fullProduct.product_status || 'original_new',
          purchase_price: fullProduct.purchase_price || '',
          sell_price: fullProduct.sell_price || '',
          wholesale_price: fullProduct.wholesale_price || '',
          discount: fullProduct.discount || 0,
          store_status: fullProduct.store_status || 'active',
          wholesale_status: fullProduct.wholesale_status || 'inactive',
          stock_quantity: fullProduct.stock_quantity || 0,
          supplier: fullProduct.supplier || '',
          category_id: fullProduct.category_id || '',
          weight: fullProduct.weight || '',
          description: fullProduct.description || '',
          image_url: fullProduct.image_url || ''
        });
      } catch (error) {
        console.error('Error fetching product details:', error);
        // Fallback to using the product data from the list
        setEditingProduct(product);
        setImagePreview(product.image_url || '');
        setAdditionalImages([]);
      }
    } else {
      setEditingProduct(null);
      setImagePreview('');
      setAdditionalImages([]);
      setAdditionalImageFiles([]);
      setFormData({
        name: '',
        sku: '',
        barcode: '',
        size: '',
        car_models: [],
        car_years: [],
        car_brands: [],
        product_status: 'original_new',
        purchase_price: '',
        sell_price: '',
        wholesale_price: '',
        discount: 0,
        store_status: 'active',
        wholesale_status: 'inactive',
        stock_quantity: 0,
        supplier: '',
        category_id: '',
        weight: '',
        description: '',
        image_url: ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    setImageFile(null);
    setImagePreview('');
    setAdditionalImages([]);
    setAdditionalImageFiles([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.name.trim()) {
      alert('الرجاء إدخال اسم المنتج');
      return;
    }
    if (!formData.sell_price || formData.sell_price === '' || isNaN(parseFloat(formData.sell_price))) {
      alert('الرجاء إدخال سعر البيع');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      let imageUrl = formData.image_url || '';
      
      // Upload main image if a new file was selected
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('image', imageFile);
        
        const uploadResponse = await axios.post(`${API_URL}/products/upload-image`, imageFormData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (uploadResponse.data.success) {
          imageUrl = uploadResponse.data.imageUrl;
        }
      }

      // Upload additional images if any
      let additionalImageUrls = [];
      if (additionalImageFiles.length > 0) {
        const imagesFormData = new FormData();
        additionalImageFiles.forEach(file => {
          imagesFormData.append('images', file);
        });

        const uploadImagesResponse = await axios.post(`${API_URL}/products/upload-images`, imagesFormData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        if (uploadImagesResponse.data.success) {
          additionalImageUrls = uploadImagesResponse.data.imageUrls;
        }
      }
      
      // Prepare data with proper type conversion
      const dataToSend = {
        ...formData,
        image_url: imageUrl,
        // Ensure empty strings are sent as empty strings for text fields
        sku: formData.sku || '',
        barcode: formData.barcode || '',
        size: formData.size || '',
        supplier: formData.supplier || '',
        description: formData.description || '',
        // Convert empty strings to null or 0 for numeric fields
        purchase_price: formData.purchase_price === '' || formData.purchase_price === null ? null : parseFloat(formData.purchase_price),
        sell_price: parseFloat(formData.sell_price), // Required field
        wholesale_price: formData.wholesale_price === '' || formData.wholesale_price === null ? null : parseFloat(formData.wholesale_price),
        discount: formData.discount === '' || formData.discount === null ? 0 : parseFloat(formData.discount),
        stock_quantity: formData.stock_quantity === '' || formData.stock_quantity === null ? 0 : parseInt(formData.stock_quantity),
        weight: formData.weight === '' || formData.weight === null ? null : parseFloat(formData.weight),
        category_id: formData.category_id === '' || formData.category_id === null ? null : parseInt(formData.category_id)
      };
      
      let productId;
      if (editingProduct) {
        await axios.put(`${API_URL}/products/${editingProduct.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        productId = editingProduct.id;
        alert('تم تحديث المنتج بنجاح');
      } else {
        const response = await axios.post(`${API_URL}/products`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        productId = response.data.data.id;
        alert('تم إضافة المنتج بنجاح');
      }

      // Add additional images if any
      if (additionalImageUrls.length > 0 && productId) {
        await axios.post(`${API_URL}/products/${productId}/images`, {
          imageUrls: additionalImageUrls
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      fetchProducts();
      closeModal();
    } catch (error) {
      console.error('Error saving product:', error);
      
      // More detailed error message
      if (error.response) {
        console.error('Error details:', error.response.data);
        const errorMessage = error.response.data.message || 'خطأ في الخادم';
        
        // Check if it's an authentication error
        if (error.response.status === 401 || error.response.status === 403) {
          alert(`${errorMessage}\n\nالرجاء تسجيل الدخول مرة أخرى كمسؤول`);
          // Optionally redirect to admin login
          // window.location.href = '/admin/login';
        } else {
          alert(`فشل حفظ المنتج: ${errorMessage}`);
        }
      } else if (error.request) {
        alert('فشل الاتصال بالخادم. تحقق من الاتصال بالإنترنت');
      } else {
        alert('فشل حفظ المنتج: ' + error.message);
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_URL}/products/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('فشل حذف المنتج');
      }
    }
  };

  const calculateFinalPrice = (sell_price, discount) => {
    return sell_price - (sell_price * discount / 100);
  };

  const exportToCSV = async () => {
    if (filteredProducts.length === 0) {
      alert('لا توجد منتجات للتصدير');
      return;
    }

    setLoading(true);
    try {
      // Fetch complete product data with images
      const token = localStorage.getItem('token');
      const productsWithImages = await Promise.all(
        filteredProducts.map(async (product) => {
          try {
            const response = await axios.get(`${API_URL}/products/admin/${product.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            // API returns { success: true, data: product }
            return response.data.data || response.data || product;
          } catch (error) {
            console.error(`Error fetching product ${product.id}:`, error);
            return product;
          }
        })
      );

      const headers = [
        'ID', 'الاسم', 'SKU', 'الباركود', 'الحجم', 'حالة المنتج', 
        'سعر الشراء', 'سعر البيع', 'سعر الجملة', 'الخصم%', 'السعر النهائي',
        'الكمية', 'حالة المتجر', 'حالة الجملة', 'المورد', 'الوزن', 'التصنيف', 'الوصف', 
        'الصورة الرئيسية', 'الصور الإضافية'
      ];

      const rows = productsWithImages.map(product => {
        // Get additional image URLs
        const additionalImages = product.images && product.images.length > 0
          ? product.images.map(img => img.image_url).join('|')
          : '';

        return [
          product.id,
          product.name,
          product.sku || '',
          product.barcode || '',
          product.size || '',
          product.product_status,
          product.purchase_price || '',
          product.sell_price,
          product.wholesale_price || '',
          product.discount,
          product.final_price || calculateFinalPrice(product.sell_price, product.discount),
          product.stock_quantity,
          product.store_status,
          product.wholesale_status,
          product.supplier || '',
          product.weight || '',
          product.category_name || '',
          product.description || '',
          product.image_url || '',
          additionalImages
        ];
      });

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `products_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('حدث خطأ أثناء تصدير الملف');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSVTemplate = () => {
    const headers = [
      'ID', 'الاسم', 'SKU', 'الباركود', 'الحجم', 'حالة المنتج', 
      'سعر الشراء', 'سعر البيع', 'سعر الجملة', 'الخصم%', 'السعر النهائي',
      'الكمية', 'حالة المتجر', 'حالة الجملة', 'المورد', 'الوزن', 'التصنيف', 'الوصف', 
      'الصورة الرئيسية', 'الصور الإضافية'
    ];

    const notesRow = [
      '(اتركه فارغ للمنتج الجديد)', 
      '(مطلوب)', 
      '(اختياري)', 
      '(اختياري)', 
      '(اختياري)', 
      '(original_new أو original_used أو commercial_new)', 
      '(اختياري)', 
      '(مطلوب)', 
      '(اختياري)', 
      '(اختياري)', 
      '(يحسب تلقائياً)', 
      '(اختياري)', 
      '(active أو inactive)', 
      '(active أو inactive)', 
      '(اختياري)', 
      '(اختياري)', 
      '(اختياري)', 
      '(اختياري)', 
      '(اختياري)',
      '(روابط مفصولة بـ |)'
    ];

    const exampleRow = [
      '', // ID - leave empty for new products
      'اسم المنتج مثال',
      'SKU123',
      '1234567890',
      'حجم متوسط',
      'original_new',
      '10000',
      '15000',
      '12000',
      '10',
      '',
      '50',
      'active',
      'inactive',
      'اسم المورد',
      '1.5',
      '',
      'وصف المنتج',
      'https://example.com/image1.jpg',
      'https://example.com/image2.jpg|https://example.com/image3.jpg'
    ];

    // Use tab delimiter for better Excel compatibility
    const csvContent = [
      headers.join('\t'),
      notesRow.join('\t'),
      exampleRow.join('\t')
    ].join('\n');

    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'product_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        console.log('Total lines in CSV:', lines.length);
        
        if (lines.length < 2) {
          alert('ملف CSV فارغ أو غير صحيح');
          return;
        }

        // Detect delimiter (comma or tab)
        const firstLine = lines[0];
        const delimiter = firstLine.includes('\t') ? '\t' : ',';
        console.log('Detected delimiter:', delimiter === '\t' ? 'TAB' : 'COMMA');

        const importedProducts = [];

        for (let i = 1; i < lines.length; i++) {
          try {
            const line = lines[i];
            let values = [];
            
            if (delimiter === '\t') {
              // Tab-separated - simple split
              values = line.split('\t').map(v => v.trim());
            } else {
              // Comma-separated - handle quoted values
              const matches = line.match(/(?:"([^"]*(?:""[^"]*)*)"|([^,]*))/g);
              if (matches) {
                values = matches.map(v => v.replace(/^"|"$/g, '').replace(/""/g, '"').trim());
              } else {
                values = line.split(',').map(v => v.trim());
              }
            }
            
            console.log(`Line ${i} - Columns: ${values.length}, ID: "${values[0]}", Name: "${values[1]}", Price: "${values[7]}"`);
            
            // Check if this is an update (has ID) or create (no ID)
            const productId = values[0] && values[0] !== '' && !isNaN(parseInt(values[0])) ? parseInt(values[0]) : null;
            const isUpdate = productId !== null;
            
            // Skip header rows - check multiple possible header indicators
            const productName = values[1]?.trim();
            const isHeaderRow = !productName || 
                               productName === '' || 
                               productName.toLowerCase().includes('اسم') ||
                               productName.toLowerCase().includes('name') ||
                               values[0]?.toLowerCase().includes('id');
            
            if (isHeaderRow) {
              console.log(`Line ${i}: Skipping - header or empty row`);
              continue;
            }
            
            // For new products, name and sell_price are required
            if (!isUpdate) {
              const sellPrice = parseFloat(values[7]);
              if (!sellPrice || isNaN(sellPrice) || sellPrice <= 0) {
                console.log(`Line ${i}: Skipping - new product requires valid sell price`);
                continue;
              }
            }
            
            // Normalize product_status to match database ENUM values
            let productStatus = values[5]?.trim() || '';
            console.log(`Line ${i}: Raw product_status: "${productStatus}"`);
            
            // Clean and normalize the status text
            productStatus = productStatus.replace(/\s+/g, ' ').trim();
            
            // Convert Arabic to English or validate English values
            if (productStatus) {
              // Check if it's Arabic and convert to English
              if (PRODUCT_STATUS_REVERSE[productStatus]) {
                productStatus = PRODUCT_STATUS_REVERSE[productStatus];
                console.log(`Line ${i}: Converted Arabic to English: "${productStatus}"`);
              }
              // Validate it's a valid English status
              else if (!['original_new', 'original_used', 'commercial_new'].includes(productStatus)) {
                console.log(`Line ${i}: Invalid product_status "${productStatus}", will use default for new products`);
                productStatus = ''; // Invalid status, will use default
              }
            }
            
            // Handle SKU - if empty or duplicate from template, make it unique or skip
            let sku = values[2]?.trim() || '';
            if (sku === 'SKU123') {
              sku = '';
            }

            // Build product object - only include fields that have values
            const product = {
              name: productName
            };
            
            // Add optional fields only if they have values
            if (sku) product.sku = sku;
            if (values[3]?.trim()) product.barcode = values[3].trim();
            if (values[4]?.trim()) product.size = values[4].trim();
            
            // Always include these for new products, optional for updates
            if (!isUpdate) {
              product.car_models = [];
              product.car_years = [];
              product.car_brands = [];
              product.product_status = productStatus || 'original_new';
              product.sell_price = parseFloat(values[7]);
              product.store_status = values[12]?.trim() || 'active';
              product.wholesale_status = values[13]?.trim() || 'inactive';
              product.stock_quantity = parseInt(values[11]) || 0;
              product.discount = parseFloat(values[9]) || 0;
              product.purchase_price = parseFloat(values[6]) || 0;
              product.wholesale_price = parseFloat(values[8]) || 0;
              product.weight = parseFloat(values[15]) || 0;
              product.category_id = null;
              product.image_url = values[18]?.trim() || '';
              // Parse additional images (column 19, pipe-separated)
              product.additional_images = values[19]?.trim() 
                ? values[19].trim().split('|').map(url => url.trim()).filter(url => url)
                : [];
            } else {
              // For updates, only add fields that have values
              if (productStatus) product.product_status = productStatus;
              if (values[7] && parseFloat(values[7]) > 0) product.sell_price = parseFloat(values[7]);
              if (values[12]?.trim()) product.store_status = values[12].trim();
              if (values[13]?.trim()) product.wholesale_status = values[13].trim();
              if (values[11] && values[11] !== '') product.stock_quantity = parseInt(values[11]) || 0;
              if (values[9] && values[9] !== '') product.discount = parseFloat(values[9]) || 0;
              if (values[6] && parseFloat(values[6]) > 0) product.purchase_price = parseFloat(values[6]);
              if (values[8] && parseFloat(values[8]) > 0) product.wholesale_price = parseFloat(values[8]);
              if (values[15] && parseFloat(values[15]) > 0) product.weight = parseFloat(values[15]);
              // Only update image_url if a non-empty value is provided
              if (values[18]?.trim()) product.image_url = values[18].trim();
              // Parse additional images (column 19, pipe-separated)
              if (values[19]?.trim()) {
                product.additional_images = values[19].trim().split('|').map(url => url.trim()).filter(url => url);
              }
            }
            
            if (values[14]?.trim()) product.supplier = values[14].trim();
            if (values[17]?.trim()) product.description = values[17].trim();
            
            console.log(`Line ${i}: ${isUpdate ? 'Updating' : 'Creating'} product:`, product);
            importedProducts.push({ ...product, _isUpdate: isUpdate, _id: productId });
          } catch (lineError) {
            console.error(`Error parsing line ${i}:`, lineError, lines[i]);
          }
        }

        console.log('Total products parsed:', importedProducts.length);

        if (importedProducts.length === 0) {
          alert('لم يتم العثور على منتجات صالحة للاستيراد.\n\nتأكد من:\n- وجود أسماء المنتجات\n- وجود أسعار البيع\n- تنسيق الملف صحيح');
          return;
        }

        const confirmed = window.confirm(
          `تم العثور على ${importedProducts.length} منتج.\n` +
          `${importedProducts.filter(p => p._isUpdate).length} للتحديث\n` +
          `${importedProducts.filter(p => !p._isUpdate).length} للإضافة\n\n` +
          `هل تريد المتابعة؟`
        );

        if (!confirmed) return;

        setImporting(true);
        setImportProgress({ current: 0, total: importedProducts.length });

        const token = localStorage.getItem('token');
        let successCount = 0;
        let errorCount = 0;
        let updateCount = 0;
        let createCount = 0;
        const errors = [];

        // Process in batches of 10 for better performance
        const BATCH_SIZE = 10;
        
        for (let i = 0; i < importedProducts.length; i += BATCH_SIZE) {
          const batch = importedProducts.slice(i, i + BATCH_SIZE);
          
          // Process batch in parallel
          const batchPromises = batch.map(async (productData) => {
            try {
              const isUpdate = productData._isUpdate;
              const productId = productData._id;
              
              // Remove internal flags before sending
              const { _isUpdate, _id, ...product } = productData;
              
              const url = isUpdate ? `${API_URL}/products/${productId}` : `${API_URL}/products`;
              const method = isUpdate ? 'PUT' : 'POST';
              
              const response = await fetch(url, {
                method: method,
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(product)
              });
              
              const responseData = await response.json();
              
              if (response.ok) {
                return {
                  success: true,
                  isUpdate,
                  name: product.name
                };
              } else {
                let errorMsg = responseData.error || responseData.message || 'Unknown error';
                
                // Make error messages more user-friendly
                if (errorMsg.includes('Duplicate entry') && errorMsg.includes('sku')) {
                  errorMsg = `SKU already exists (${product.sku})`;
                } else if (errorMsg.includes('Duplicate entry') && errorMsg.includes('barcode')) {
                  errorMsg = `Barcode already exists (${product.barcode})`;
                } else if (errorMsg.includes('Data truncated')) {
                  errorMsg = 'Invalid data format';
                } else if (errorMsg.includes('not found') || response.status === 404) {
                  errorMsg = `Product ID ${productId} not found`;
                }
                
                return {
                  success: false,
                  name: product.name,
                  error: errorMsg
                };
              }
            } catch (err) {
              return {
                success: false,
                name: productData.name,
                error: err.message
              };
            }
          });
          
          // Wait for batch to complete
          const batchResults = await Promise.all(batchPromises);
          
          // Process results
          batchResults.forEach(result => {
            if (result.success) {
              successCount++;
              if (result.isUpdate) {
                updateCount++;
                console.log(`✓ Successfully updated: ${result.name}`);
              } else {
                createCount++;
                console.log(`✓ Successfully created: ${result.name}`);
              }
            } else {
              errorCount++;
              errors.push(`${result.name}: ${result.error}`);
              console.error(`✗ Error importing product: ${result.name}`);
            }
          });
          
          // Update progress
          setImportProgress({ 
            current: Math.min(i + BATCH_SIZE, importedProducts.length), 
            total: importedProducts.length 
          });
        }

        setImporting(false);
        setImportProgress({ current: 0, total: 0 });
        
        let message = `تم الاستيراد!\n\nنجح: ${successCount} (${createCount} جديد، ${updateCount} محدث)\nفشل: ${errorCount}`;
        if (errors.length > 0 && errors.length <= 5) {
          message += '\n\nالأخطاء:\n' + errors.join('\n');
        }
        alert(message);
        fetchProducts();
      } catch (error) {
        console.error('Error parsing CSV:', error);
        setImporting(false);
        setImportProgress({ current: 0, total: 0 });
        alert('خطأ في قراءة الملف. تأكد من أن الملف بتنسيق CSV صحيح.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Import Progress Overlay */}
      {importing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mb-4">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
              </div>
              <h3 className="text-xl font-bold mb-2">جاري استيراد المنتجات...</h3>
              <p className="text-gray-600 mb-4">
                {importProgress.current} / {importProgress.total}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500">
                {Math.round((importProgress.current / importProgress.total) * 100)}% مكتمل
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">إدارة المنتجات</h1>
        <div className="flex gap-3">
          <button
            onClick={downloadCSVTemplate}
            className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition flex items-center gap-2"
            title="تحميل نموذج CSV للاستيراد"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            نموذج CSV
          </button>
          <input
            type="file"
            accept=".csv"
            onChange={handleImportCSV}
            className="hidden"
            id="csv-import"
          />
          <label
            htmlFor="csv-import"
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition cursor-pointer flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            استيراد CSV
          </label>
          <button
            onClick={exportToCSV}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            تصدير CSV
          </button>
          <button
            onClick={() => openModal()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            + إضافة منتج جديد
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-3">
            <div className="relative">
              <input
                type="text"
                placeholder="بحث بالاسم، SKU، الباركود، أو المورد..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <svg
                className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          <div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">كل التصنيفات</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">كل الحالات</option>
              <option value="active">نشط</option>
              <option value="inactive">غير نشط</option>
            </select>
          </div>

          <div>
            <select
              value={filterCarBrand}
              onChange={(e) => setFilterCarBrand(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">كل ماركات السيارات</option>
              {carBrands.map(brand => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-2">
            <select
              value={filterCarModel}
              onChange={(e) => setFilterCarModel(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">كل موديلات السيارات</option>
              {carModels.map(model => (
                <option key={model.id} value={model.id}>
                  {model.brand_name} - {model.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="mt-3 flex justify-between items-center text-sm text-gray-600">
          <div>
            عرض {startIndex + 1} - {Math.min(endIndex, filteredProducts.length)} من {filteredProducts.length} منتج
            {searchQuery || filterCategory || filterStatus || filterCarModel || filterCarBrand ? ` (تم التصفية من ${products.length})` : ''}
          </div>
          <div className="flex items-center gap-2">
            <label>عناصر لكل صفحة:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">ID</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">الاسم</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">SKU</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">الباركود</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">حالة المنتج</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">سعر الشراء</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">سعر البيع</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">الخصم%</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">السعر النهائي</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">الكمية</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">حالة المتجر</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">إجراءات</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentProducts.length === 0 ? (
              <tr>
                <td colSpan="12" className="px-4 py-8 text-center text-gray-500">
                  {searchQuery || filterCategory || filterStatus
                    ? 'لم يتم العثور على منتجات تطابق معايير البحث'
                    : 'لا توجد منتجات'}
                </td>
              </tr>
            ) : (
              currentProducts.map(product => (
                <tr key={product.id} className="hover:bg-gray-50">
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{product.id}</td>
                <td className="px-4 py-4 text-sm text-gray-900">{product.name}</td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{product.sku || '-'}</td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{product.barcode || '-'}</td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{PRODUCT_STATUS[product.product_status] || product.product_status || '-'}</td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {product.purchase_price ? `${product.purchase_price} IQD` : '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {product.sell_price} IQD
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{product.discount}%</td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                  {product.final_price || calculateFinalPrice(product.sell_price, product.discount)} IQD
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{product.stock_quantity}</td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    product.store_status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {product.store_status === 'active' ? 'نشط' : 'غير نشط'}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => openModal(product)}
                    className="text-blue-600 hover:text-blue-900 ml-4"
                  >
                    تعديل
                  </button>
                  <button
                    onClick={() => handleDelete(product.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    حذف
                  </button>
                </td>
              </tr>
            ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center items-center gap-2">
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            الأولى
          </button>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            السابق
          </button>
          
          <div className="flex gap-1">
            {[...Array(Math.min(5, totalPages))].map((_, idx) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = idx + 1;
              } else if (currentPage <= 3) {
                pageNum = idx + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + idx;
              } else {
                pageNum = currentPage - 2 + idx;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => goToPage(pageNum)}
                  className={`px-3 py-1 border rounded ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            التالي
          </button>
          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            الأخيرة
          </button>
          
          <span className="mr-4 text-sm text-gray-600">
            صفحة {currentPage} من {totalPages}
          </span>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingProduct ? 'تعديل المنتج' : 'إضافة منتج جديد'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">اسم المنتج *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SKU/الكود</label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">الباركود</label>
                  <input
                    type="text"
                    name="barcode"
                    value={formData.barcode}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">الحجم</label>
                  <input
                    type="text"
                    name="size"
                    value={formData.size}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Car Details */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-4">تفاصيل السيارة</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Car Models */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">موديلات السيارات</label>
                    <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto bg-gray-50">
                      <div className="space-y-2">
                        {carModels.map((model) => (
                          <label key={model.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded">
                            <input
                              type="checkbox"
                              checked={ensureArray(formData.car_models).includes(model.id)}
                              onChange={() => handleCheckboxChange('car_models', model.id)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm">{model.brand_name} - {model.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">حدد الموديلات المتوافقة</p>
                  </div>

                  {/* Car Years */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">سنوات الموديل</label>
                    <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto bg-gray-50">
                      <div className="grid grid-cols-2 gap-2">
                        {carYears.map((year) => (
                          <label key={year.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded">
                            <input
                              type="checkbox"
                              checked={ensureArray(formData.car_years).includes(year.id)}
                              onChange={() => handleCheckboxChange('car_years', year.id)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm">{year.year}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">حدد السنوات المتوافقة</p>
                  </div>

                  {/* Car Brands */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ماركات السيارات</label>
                    <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto bg-gray-50">
                      <div className="space-y-2">
                        {carBrands.map((brand) => (
                          <label key={brand.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded">
                            <input
                              type="checkbox"
                              checked={ensureArray(formData.car_brands).includes(brand.id)}
                              onChange={() => handleCheckboxChange('car_brands', brand.id)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm">{brand.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">حدد العلامات المتوافقة</p>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-4">الأسعار</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">سعر الشراء</label>
                    <input
                      type="number"
                      step="0.01"
                      name="purchase_price"
                      value={formData.purchase_price}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">سعر البيع *</label>
                    <input
                      type="number"
                      step="0.01"
                      name="sell_price"
                      value={formData.sell_price}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">سعر الجملة</label>
                    <input
                      type="number"
                      step="0.01"
                      name="wholesale_price"
                      value={formData.wholesale_price}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">الخصم %</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      name="discount"
                      value={formData.discount}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {formData.sell_price && (
                  <div className="mt-4 p-4 bg-green-50 rounded-lg">
                    <p className="text-lg font-bold text-green-800">
                      السعر النهائي بعد الخصم: {calculateFinalPrice(parseFloat(formData.sell_price), parseFloat(formData.discount))} IQD
                    </p>
                  </div>
                )}
              </div>

              {/* Status & Stock */}
              <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">حالة المنتج</label>
                  <select
                    name="product_status"
                    value={formData.product_status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="original_new">اصلي جديد</option>
                    <option value="original_used">اصلي مستعمل</option>
                    <option value="commercial_new">جديد تجاري</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">حالة المتجر</label>
                  <select
                    name="store_status"
                    value={formData.store_status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">نشط</option>
                    <option value="inactive">غير نشط</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">حالة الجملة</label>
                  <select
                    name="wholesale_status"
                    value={formData.wholesale_status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">نشط</option>
                    <option value="inactive">غير نشط</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">الكمية</label>
                  <input
                    type="number"
                    name="stock_quantity"
                    value={formData.stock_quantity}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">المورد</label>
                  <input
                    type="text"
                    name="supplier"
                    value={formData.supplier}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">الوزن (كجم)</label>
                  <input
                    type="number"
                    step="0.001"
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Category & Description */}
              <div className="border-t pt-4 grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">التصنيف</label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">اختر التصنيف</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">الوصف</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">صورة المنتج الرئيسية</label>
                  
                  <div className="flex items-start gap-4">
                    <label className="cursor-pointer group relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <div className={`w-32 h-32 rounded-lg border-2 border-dashed flex flex-col items-center justify-center transition-all ${
                        imagePreview 
                          ? 'border-gray-300' 
                          : 'border-gray-400 hover:border-blue-500 hover:bg-blue-50'
                      }`}>
                        {imagePreview ? (
                          <>
                            <img 
                              src={imagePreview.startsWith('blob:') ? imagePreview : `https://api.iraq-parts.com${imagePreview}`}
                              alt="Preview" 
                              className="w-full h-full object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white">
                              <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span className="text-xs font-semibold">تغيير</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <svg className="w-8 h-8 text-gray-400 group-hover:text-blue-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="text-xs text-gray-600 font-semibold group-hover:text-blue-500">اختر صورة</span>
                          </>
                        )}
                      </div>
                    </label>

                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 mb-1">أو استخدم رابط صورة</label>
                      <input
                        type="text"
                        name="image_url"
                        value={formData.image_url}
                        onChange={handleInputChange}
                        placeholder="https://example.com/image.jpg"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      {imagePreview && (
                        <button
                          type="button"
                          onClick={removeImage}
                          className="mt-2 flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-semibold"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          إزالة
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Additional Images Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">صور إضافية (حتى 10 صور)</label>
                  
                  <div className="space-y-3">
                    {/* Upload Button */}
                    <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleAdditionalImagesChange}
                        className="hidden"
                      />
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">إضافة صور إضافية</span>
                    </label>

                    {/* Image Previews Grid */}
                    {additionalImages.length > 0 && (
                      <div className="grid grid-cols-4 gap-3">
                        {additionalImages.map((image, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={image.startsWith('blob:') || image.startsWith('http') ? image : `https://api.iraq-parts.com${image}`}
                              alt={`Additional ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeAdditionalImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                            <div className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-xs px-2 py-0.5 rounded">
                              {index + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-4 border-t pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  {editingProduct ? 'تحديث' : 'إضافة'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
